import { Button, Checkbox, InfoIcon, Input, Modal, Select } from "@bp/ui-components";
import type { SelectOptionType } from "@bp/ui-components/dist/@bp/lib/Select/Select";
import { useState } from "react";
import type { MultiValue, SingleValue } from "react-select";
import { useMutation } from "urql";
import { graphql } from "../../../client/gql";
import { InfoBox } from "../../../components/InfoBox/InfoBox";
import { sendInvitation } from "../../../utils/invitation";
import { showSuccessToast, showUserErrorToast } from "../../../utils/toast";
import css from "../orgList.module.scss";

const AddMemberModalCreateProfile = graphql(`
  mutation AddMemberModal_CreateProfile($input: [ProfileCreateInput!]!) {
    createProfiles(input: $input) {
      profiles {
        uuid
      }
    }
  }
`);

// Profile roles only (@bp/pim-auth-constants ProfileRoles). ACL/admin roles
// (ADMIN, *_ADMIN, …) are a separate concept and are not assigned here.
const ROLE_OPTIONS: SelectOptionType[] = [
  { value: "TEACHER", label: "Lehrkraft" },
  { value: "STUDENT", label: "Schüler:in" },
  { value: "PARENT", label: "Eltern" },
  { value: "OTHER", label: "Sonstige" },
];

type Props = {
  org: { uuid: string; name: string };
  /** URL of the org's Bildungsplattform, sent with the invitation as its frontend URL. */
  invitationUrl?: string;
  /** Role to preselect (from the "Hinzufügen" role dropdown). */
  initialRole?: string;
  onClose: () => void;
  onAdded: () => void;
};

export function AddMemberModal({ org, invitationUrl, initialRole, onClose, onAdded }: Props) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(initialRole ?? "TEACHER");
  const [createAccount, setCreateAccount] = useState(false);
  const [busy, setBusy] = useState(false);
  const [, createProfile] = useMutation(AddMemberModalCreateProfile);

  const emailTrimmed = email.trim();
  const valid =
    firstName.trim() !== "" && lastName.trim() !== "" && (!createAccount || emailTrimmed !== "");

  const add = async () => {
    setBusy(true);
    const res = await createProfile({
      input: [
        {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: emailTrimmed || undefined,
          organization: { connect: { where: { node: { uuid: org.uuid } } } },
          roles: {
            Organization: {
              connect: [{ edge: { names: [role] }, where: { node: { uuid: org.uuid } } }],
            },
          },
          ...(createAccount && emailTrimmed
            ? { user: { create: { node: { email: emailTrimmed, emailVerified: false } } } }
            : {}),
        },
      ],
    });
    if (res.error) {
      setBusy(false);
      showUserErrorToast({ text: "Anlegen fehlgeschlagen", error: res.error });
      return;
    }
    const newProfileUuid = res.data?.createProfiles?.profiles?.[0]?.uuid;
    if (createAccount && emailTrimmed && newProfileUuid) {
      try {
        await sendInvitation(newProfileUuid, emailTrimmed, "bp", invitationUrl);
      } catch (error) {
        setBusy(false);
        showUserErrorToast({ text: "Profil angelegt, aber Einladung fehlgeschlagen", error });
        onAdded();
        return;
      }
    }
    setBusy(false);
    showSuccessToast(
      createAccount && emailTrimmed
        ? `${firstName} ${lastName} angelegt — Einladung gesendet`
        : `${firstName} ${lastName} angelegt`,
    );
    onAdded();
  };

  return (
    <Modal
      isOpen
      ariaHideApp={false}
      hideFooter={false}
      width="s"
      title={`Mitglied hinzufügen — ${org.name}`}
      onRequestClose={onClose}
      footer={
        <div className={css.foot}>
          <span className={css.footHint}>Das Profil wird in dieser Organisation angelegt.</span>
          <div className={css.footActions}>
            <Button hierarchy="tertiary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button hierarchy="primary" disabled={!valid || busy} onClick={() => void add()}>
              Hinzufügen
            </Button>
          </div>
        </div>
      }
    >
      <div className={css.stack}>
        <Input
          name="firstName"
          label="Vorname"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <Input
          name="lastName"
          label="Nachname"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
        <Input
          name="email"
          label={createAccount ? "E-Mail" : "E-Mail (optional)"}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Select
          name="role"
          label="Rolle"
          options={ROLE_OPTIONS}
          value={ROLE_OPTIONS.find((o) => o.value === role) ?? null}
          isSearchable={false}
          onChange={(opt: MultiValue<SelectOptionType> | SingleValue<SelectOptionType>) =>
            setRole(String((opt as SelectOptionType).value))
          }
        />
        <div className={css.toggleRow}>
          <Checkbox
            name="createAccount"
            checked={createAccount}
            onChange={(e) => setCreateAccount(e.target.checked)}
          />
          <span>
            <span className={css.toggleRowTitle}>Konto anlegen &amp; einladen</span>
            <span className={css.toggleRowSub}>
              {createAccount
                ? "Einladung geht an die E-Mail-Adresse."
                : "Nur Profil, kein Login (z. B. für Stellvertretung)."}
            </span>
          </span>
        </div>
        <InfoBox tone="primary" icon={<InfoIcon style={{ fontSize: 15 }} />}>
          <span style={{ fontSize: 12 }}>
            Es entsteht ein <strong>Profil in {org.name}</strong>
            {createAccount
              ? " und ein globales Benutzerkonto (sofern noch keins existiert)."
              : ". Ein Login kann später ergänzt werden."}
          </span>
        </InfoBox>
      </div>
    </Modal>
  );
}
